import {
  DASH_CHAT_MODE,
  MAIN_CHAT_MODE,
  RESEARCH_CHAT_MODE,
  ULTRA_CHAT_MODE,
} from '@littlebird/sdk';
import { toast } from 'sonner';

import { researchQuotaLabel } from '@/features/chat/chat.hooks';
import { useAppShortcut } from '@/features/shortcuts';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { userStore } from '@/stores/user.store';
import { AppCapability, inElectron, isCapable } from '@/utils/capabilities';

type ChatModeHotkeyOptions = {
  mode: string;
  setMode: (mode: string) => void;
  scopes?: string[];
};

export function useRegisterDefaultModeHotkey({
  mode,
  setMode,
  scopes,
}: ChatModeHotkeyOptions) {
  const isEnabled =
    isCapable(AppCapability.HotKeys) &&
    inElectron() &&
    !subscriptionStore.isFree &&
    mode !== MAIN_CHAT_MODE;

  useAppShortcut(
    'chat.modeDefault',
    () => {
      if (!isEnabled) return;
      setMode(MAIN_CHAT_MODE);
    },
    {
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
      enabled: isEnabled,
      scopes,
    },
    [isEnabled, setMode]
  );
}

export function useRegisterDashModeHotkey({
  mode,
  setMode,
  scopes,
}: ChatModeHotkeyOptions) {
  useAppShortcut(
    'chat.modeQuick',
    () => {
      if (!isCapable(AppCapability.HotKeys)) return;
      if (subscriptionStore.isFree) return;

      setMode(mode === DASH_CHAT_MODE ? MAIN_CHAT_MODE : DASH_CHAT_MODE);
    },
    {
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
      scopes,
    },
    [mode, setMode]
  );
}

export function useRegisterUltraModeHotkey({
  mode,
  setMode,
  scopes,
}: ChatModeHotkeyOptions) {
  useAppShortcut(
    'chat.modeUltra',
    () => {
      if (!isCapable(AppCapability.HotKeys)) return;
      if (!(subscriptionStore.isPro || userStore.isInternalUser)) return;

      if (mode !== ULTRA_CHAT_MODE && subscriptionStore.isResearchLimitHit) {
        toast.info(`Daily ${researchQuotaLabel()} limit reached`);
        return;
      }

      setMode(mode === ULTRA_CHAT_MODE ? MAIN_CHAT_MODE : ULTRA_CHAT_MODE);
    },
    {
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
      scopes,
    },
    [mode, setMode]
  );
}

export function useRegisterResearchModeHotkey({
  mode,
  setMode,
  scopes,
}: ChatModeHotkeyOptions) {
  useAppShortcut(
    'chat.modeMax',
    () => {
      if (!isCapable(AppCapability.HotKeys)) return;
      if (subscriptionStore.isFree) return;

      const isNonDefaultResearchAgent =
        mode !== RESEARCH_CHAT_MODE &&
        userStore.user?.chat_mode_details?.[mode]?.response_mode === 'research';

      if (isNonDefaultResearchAgent) {
        toast.info(
          "You're on a custom Max agent, you'll need to change it from the agent dropdown"
        );
        return;
      }

      if (mode !== RESEARCH_CHAT_MODE && subscriptionStore.isResearchLimitHit) {
        toast.info(`Daily ${researchQuotaLabel()} limit reached`);
        return;
      }

      setMode(
        mode === RESEARCH_CHAT_MODE ? MAIN_CHAT_MODE : RESEARCH_CHAT_MODE
      );
    },
    {
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
      scopes,
    },
    [mode, setMode]
  );
}
